/* eslint-disable consistent-return */
/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { MaskedInputForm } from '../../inputs/forms/MaskedInputForm';
import { CepField } from '../../inputs/forms/CepField';
import { ImageInputForm } from '../../inputs/forms/ImageInputForm';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import { NewResponsible, ResponsiblePicker } from '../../inputs/forms/ResponsiblePicker';
import { NewResponsibleDialog } from '../../inputs/forms/ResponsiblePicker/NewResponsibleDialog';
import { FormActions, FormRow, FormSection, FormShell } from '../../ui';
import { applySecretaryResponsiblePayload, SecretaryResponsibleMode } from './payload';
import { getSecretaryResponsibleValidationShape } from './validation';

interface SecretaryFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function SecretaryForm({ defaultValues }: SecretaryFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/secretaries';
  const { requestApi: reqApiSecretaries } = useFetch(baseUrl);
  const { requestApi: reqApiCodepost, error: errorCodepost } = useFetch('/v1/client/address');
  const { requestApi: reqApiUsers } = useFetch('/v1/admin/users');
  const reqApiUsersRef = React.useRef(reqApiUsers);
  reqApiUsersRef.current = reqApiUsers;
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [images, setImages] = useState<any>('');
  const [loadingCodepost, setLoadingCodepost] = useState<any>(false);
  const [uploading, setUploading] = useState<any>(false);
  const [address, setAddress] = useState<any>({});
  const [isFirstRender, setIsFirstRender] = useState(true);
  // Estados para busca e seleção de responsáveis
  const [selectedExistingUsers, setSelectedExistingUsers] = useState<any[]>([]);
  const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState<any[]>([]);
  const [loadingEligibleUsers, setLoadingEligibleUsers] = useState(false);
  const [responsibleMode, setResponsibleMode] = useState<SecretaryResponsibleMode>(
    itemId ? 'manual' : 'existing'
  );

  const optionsOrgan = [
    {
      label: 'Federal',
      value: 'FEDERAL',
    },
    {
      label: 'Estadual',
      value: 'ESTADUAL',
    },
    {
      label: 'Municipal',
      value: 'MUNICIPAL',
    },
  ];

  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues?.name ?? '',
      placeholder: 'Nome da secretaria',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'site',
      value: defaultValues?.site ?? '',
      placeholder: 'Site da secretaria',
      label: 'Site',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'organ',
      value: defaultValues?.organ
        ? optionsOrgan.find((item) => item.value === defaultValues?.organ)
        : null,
      placeholder: 'Órgão da secretaria',
      label: 'Órgão',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'cell_phone',
      value: defaultValues?.cell_phone ?? '',
      placeholder: 'Telefone da secretaria',
      label: 'Telefone',
      yup: Yup.string(),
    },
    {
      name: 'code_post',
      value: defaultValues?.address?.code_post ?? '',
      placeholder: 'CEP da secretaria',
      label: 'CEP',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'city_id',
      value: defaultValues?.address?.city?.id ?? '',
      placeholder: '',
      label: 'Cidade',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'city_name',
      value: defaultValues?.address?.city?.name ?? '',
      placeholder: 'Cidade da secretaria',
      label: 'Cidade',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'state_name',
      value: defaultValues?.address?.city?.state?.name ?? '',
      placeholder: 'Estado da secretaria',
      label: 'Estado',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'street',
      value: defaultValues?.address?.street ?? '',
      placeholder: 'Rua da secretaria',
      label: 'Rua',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'number',
      value: defaultValues?.address?.number ?? '',
      placeholder: 'Número da secretaria',
      label: 'Número',
      yup: Yup.string(),
    },
    {
      name: 'district',
      value: defaultValues?.address?.district ?? '',
      placeholder: 'Bairro da secretaria',
      label: 'Bairro',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'complement',
      value: defaultValues?.address?.complement ?? '',
      placeholder: 'Complemento da secretaria',
      label: 'Complemento',
      yup: Yup.string(),
    },
    {
      name: 'responsible',
      value: defaultValues?.responsible ?? '',
      placeholder: 'Nome do responsável',
      label: 'Nome',
      yup: Yup.string(),
    },
    {
      name: 'email',
      value: defaultValues?.email ?? '',
      placeholder: 'Email do responsável',
      label: 'Email',
      yup: Yup.string().email('Email inválido'),
    },
    {
      name: 'responsible_phone',
      value: defaultValues?.responsible_phone ?? '',
      placeholder: 'Telefone do responsável',
      label: 'Telefone',
      yup: Yup.string(),
    },
    {
      name: 'function',
      value: defaultValues?.function ?? 'Responsável pela secretaria',
      placeholder: 'Função do responsável',
      label: 'Função',
      yup: Yup.string(),
    },
    {
      name: 'image',
      value: defaultValues?.image?.original ?? null,
      placeholder: '',
      label: 'Brasão',
      yup: Yup.mixed().nullable(),
    },
    {
      name: 'status',
      value: defaultValues?.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  defaultValuesSchema.responsible_user_id = defaultValues?.responsible_user?.id ?? null;

  const requiresManualResponsible = !!itemId || responsibleMode === 'manual';
  const requiresExistingResponsible = !itemId && responsibleMode === 'existing';
  const schema = useMemo(
    () =>
      Yup.object().shape({
        ...yupschema,
        ...getSecretaryResponsibleValidationShape(),
      }),
    [requiresManualResponsible, requiresExistingResponsible]
  );

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    control,
    trigger,
    clearErrors,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm({
    resolver: yupResolver(schema),
    context: { requiresManualResponsible, requiresExistingResponsible },
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const cellphone = formInputs.find((field) => field.name === 'cell_phone');
  const site = formInputs.find((field) => field.name === 'site');
  const organ = formInputs.find((field) => field.name === 'organ');
  const codePost = formInputs.find((field) => field.name === 'code_post');
  const city = formInputs.find((field) => field.name === 'city_name');
  const state = formInputs.find((field) => field.name === 'state_name');
  const street = formInputs.find((field) => field.name === 'street');
  const number = formInputs.find((field) => field.name === 'number');
  const district = formInputs.find((field) => field.name === 'district');
  const complement = formInputs.find((field) => field.name === 'complement');
  const image = formInputs.find((field) => field.name === 'image');
  const status = formInputs.find((field) => field.name === 'status');

  // Função para recarregar responsáveis vinculados
  const reloadResponsibles = React.useCallback(async () => {
    if (!itemId) return;

    try {
      const response = await reqApiUsers(
        'get',
        {},
        `/v1/admin/secretaries/${itemId}/additional-responsibles`
      );
      if (response?.data?.data && Array.isArray(response.data.data)) {
        setSelectedExistingUsers(response.data.data);
      }
    } catch {
      setSelectedExistingUsers([]);
    }
  }, [itemId, reqApiUsers]);

  const onSubmit = async (values: any) => {
    const selectedUserIds = selectedExistingUsers.map((user) => user.id);
    const params = applySecretaryResponsiblePayload({
      payload: {
        ...values,
        organ: values.organ.value,
        address: {
          code_post: values.code_post,
          city_id: Number(values.city_id),
          street: values.street,
          number: values.number,
          district: values.district,
          complement: values.complement,
        },
        code_post: _,
        city_id: _,
        city_name: _,
        state_name: _,
        street: _,
        number: _,
        district: _,
        complement: _,
        image: _,
      },
      mode: responsibleMode,
      isEditing: !!itemId,
      selectedUserIds,
    });

    const { status: resStatus, data } = await reqApiSecretaries(
      defaultValues?.name ? 'put' : 'post',
      params,
      defaultValues?.name ? `${baseUrl}/${itemId}` : null
    );

    if (resStatus !== 200 && resStatus !== 201) return;

    // Se chegou aqui, foi sucesso - fazer upload de imagem se necessário
    if (images) {
      setUploading(true);
      const apiUrlId = itemId ?? data?.data?.id;
      await reqApiSecretaries('put', { file: images.toString() }, `${baseUrl}/${apiUrlId}/images`);
      setUploading(false);
    }

    await reloadResponsibles();
    navigate('/secretaries');
  };

  const getCep = async (cep) => {
    const { data, status: resStatus } = await reqApiCodepost('get', { params: { code_post: cep } });
    if (resStatus === 200) {
      setAddress(data?.data);
    } else {
      setAddress({});
    }
    setLoadingCodepost(false);
  };

  // Buscar usuários com debounce
  const searchUsers = React.useMemo(
    () =>
      _.debounce(async (searchTerm: string) => {
        if (!searchTerm || searchTerm.length < 3) {
          setUserSearchResults([]);
          return;
        }

        setSearchingUsers(true);
        try {
          const params: any = {
            search: searchTerm,
            group_id: 6,
          };
          const response = await reqApiUsersRef.current('get', { params });
          setUserSearchResults(response?.data?.data || []);
        } catch {
          setUserSearchResults([]);
        } finally {
          setSearchingUsers(false);
        }
      }, 500),
    []
  );

  const handleSelectUser = (user: any) => {
    const alreadySelected = selectedExistingUsers.find((u) => u.id === user.id);
    if (alreadySelected) {
      return;
    }
    if (!itemId && responsibleMode === 'manual') {
      setResponsibleMode('existing');
      setValue('responsible', '');
      setValue('email', '');
      setValue('responsible_phone', '');
    }

    const updated = [...selectedExistingUsers, user];
    setSelectedExistingUsers(updated);
    setValue('responsible_user_id', updated[0]?.id ?? null, { shouldValidate: true });
    setUserSearchResults([]);
    clearErrors('responsible_user_id');
  };

  const handleRemoveUser = (userId: number) => {
    const updated = selectedExistingUsers.filter((u) => u.id !== userId);
    setSelectedExistingUsers(updated);
    setValue('responsible_user_id', updated[0]?.id ?? null, { shouldValidate: true });
  };

  const handleUserSearchTerm = (searchTerm: string) => {
    if (searchTerm.length === 0) {
      setUserSearchResults([]);
      return;
    }
    searchUsers(searchTerm);
  };

  const [newResponsibleOpen, setNewResponsibleOpen] = useState(false);
  const manualValues = watch(['responsible', 'email', 'responsible_phone', 'function']);
  const manualResponsible: NewResponsible | null =
    requiresManualResponsible && (manualValues[0] || manualValues[1])
      ? {
          name: manualValues[0] ?? '',
          email: manualValues[1] ?? '',
          phone: manualValues[2] ?? '',
          function: manualValues[3] ?? '',
        }
      : null;
  const manualResponsibleError =
    requiresManualResponsible && !manualResponsible
      ? (errors as any).responsible?.message ??
        (errors as any).email?.message ??
        (errors as any).responsible_phone?.message
      : undefined;

  const openNewResponsibleDialog = () => setNewResponsibleOpen(true);

  const applyManualResponsible = (value: NewResponsible) => {
    if (!itemId) {
      setResponsibleMode('manual');
      setSelectedExistingUsers([]);
      setUserSearchResults([]);
      setValue('responsible_user_id', null);
    }
    setValue('responsible', value.name, { shouldValidate: true, shouldDirty: true });
    setValue('email', value.email, { shouldValidate: true, shouldDirty: true });
    setValue('responsible_phone', value.phone, { shouldValidate: true, shouldDirty: true });
    setValue('function', value.function ?? 'Responsável pela secretaria', {
      shouldValidate: true,
      shouldDirty: true,
    });
    clearErrors(['responsible', 'email', 'responsible_phone', 'function', 'responsible_user_id']);
  };

  const clearManualResponsible = () => {
    setValue('responsible', '');
    setValue('email', '');
    setValue('responsible_phone', '');
    setResponsibleMode('existing');
  };

  useEffect(() => {
    if (defaultValues?.name) {
      reset(defaultValuesSchema);
      getCep(watch('code_post').replace(/\D/g, ''));
    }
  }, [defaultValues]);

  // Carregar responsáveis existentes quando estiver editando
  useEffect(() => {
    const isEditing = !!itemId;
    if (isEditing && itemId) {
      const loadResponsibles = async () => {
        try {
          const users: any[] = [];

          // Carregar responsáveis vinculados à secretaria
          try {
            const response = await reqApiUsers(
              'get',
              {},
              `/v1/admin/secretaries/${itemId}/additional-responsibles`
            );
            if (response?.data?.data && Array.isArray(response.data.data)) {
              users.push(...response.data.data);
            }
          } catch {
            users.length = 0;
          }

          if (users.length > 0) {
            setSelectedExistingUsers(users);
          }
        } catch {
          setSelectedExistingUsers([]);
        }
      };

      loadResponsibles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  useEffect(() => {
    setValue('city_id', address?.city?.id);
    setValue('city_name', address?.city?.name);
    setValue('state_name', address?.city?.state?.name);
    setValue('street', address?.street);
    setValue('district', address?.district);
  }, [address]);

  useEffect(() => {
    if (watch('code_post').replace(/\D/g, '').length < 8) {
      setAddress(null);
      return;
    }
    if (!isFirstRender) {
      setLoadingCodepost(true);
      const timeout = setTimeout(() => {
        getCep(watch('code_post').replace(/\D/g, ''));
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [watch('code_post')]);

  // Carregar lista de usuários elegíveis para responsável de secretaria
  useEffect(() => {
    const loadEligibleUsers = async () => {
      setLoadingEligibleUsers(true);
      try {
        // TODO: Melhorar backend para filtrar usuários já vinculados a outras secretarias
        // Buscar usuários com group_id=6 (Responsável pela Secretaria)
        const params = {
          group_id: 6,
          per_page: 50, // Limitar resultados
        };
        const response = await reqApiUsers('get', { params });
        const users = response?.data?.data || [];

        // Por enquanto, exibir todos os usuários com group_id=6
        // Futuramente, filtrar aqui os que já estão em outras secretarias
        setEligibleUsers(users);
      } catch {
        setEligibleUsers([]);
      } finally {
        setLoadingEligibleUsers(false);
      }
    };

    loadEligibleUsers();
  }, []);

  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  useEffect(
    () => () => {
      searchUsers.cancel();
    },
    [searchUsers]
  );

  useEffect(() => {
    if (!isSubmitted) return;
    trigger(['responsible', 'email', 'responsible_phone', 'function', 'responsible_user_id']);
  }, [responsibleMode, selectedExistingUsers.length, trigger, isSubmitted]);

  const isEdit = Boolean(defaultValues?.name);

  return (
    <FormShell
      title={isEdit ? 'Editar Secretaria' : 'Criar Secretaria'}
      backTo="/secretaries"
      backLabel="Secretarias"
    >
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações Básicas">
          <FormRow>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <SelectInputForm
              name={organ?.name}
              label={organ?.label}
              options={optionsOrgan}
              placeholder={organ?.placeholder}
              isRequired
              control={control}
              errors={errors}
            />
          </FormRow>

          <FormRow>
            <MaskedInputForm
              name={cellphone?.name}
              label={cellphone?.label}
              placeholder={cellphone?.placeholder}
              control={control}
              errors={errors}
              mask="(99) 99999-9999"
            />
            <InputForm
              name={site?.name}
              label={site?.label}
              placeholder={site?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </FormRow>
        </FormSection>

        <FormSection title="Endereço">
          <FormRow>
            <CepField
              name={codePost?.name}
              label={codePost?.label}
              placeholder={codePost?.placeholder}
              control={control}
              errors={errors}
              isLoading={loadingCodepost}
              notFoundMessage={errorCodepost?.status === 404 ? errorCodepost?.msg : undefined}
            />
            <InputForm
              name={city?.name}
              label={city?.label}
              placeholder={city?.placeholder}
              isDisabled
              isRequired
              register={register}
              errors={errors}
            />
            <InputForm
              name={state?.name}
              label={state?.label}
              placeholder={state?.placeholder}
              isDisabled
              isRequired
              register={register}
              errors={errors}
            />
          </FormRow>

          <FormRow>
            <InputForm
              name={street?.name}
              label={street?.label}
              placeholder={street?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <InputForm
              name={number?.name}
              label={number?.label}
              placeholder={number?.placeholder}
              type="number"
              register={register}
              errors={errors}
            />
            <InputForm
              name={district?.name}
              label={district?.label}
              placeholder={district?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <InputForm
              name={complement?.name}
              label={complement?.label}
              placeholder={complement?.placeholder}
              register={register}
              errors={errors}
            />
          </FormRow>
        </FormSection>

        <FormSection
          title="Responsável"
          description={
            itemId
              ? 'Edite o responsável principal ou vincule usuários adicionais a esta secretaria.'
              : 'Busque um usuário existente ou cadastre um novo responsável. O primeiro da lista é o principal.'
          }
        >
          <ResponsiblePicker
            label={itemId ? 'Responsáveis vinculados' : 'Responsáveis'}
            searchPlaceholder="Buscar responsável por nome ou email"
            isSearching={searchingUsers}
            isLoadingEligible={loadingEligibleUsers}
            searchResults={userSearchResults}
            eligibleUsers={eligibleUsers}
            selectedUsers={selectedExistingUsers}
            newResponsible={manualResponsible}
            error={(errors as any).responsible_user_id?.message || manualResponsibleError}
            onSearchChange={handleUserSearchTerm}
            onSelectUser={handleSelectUser}
            onRemoveUser={handleRemoveUser}
            onCreateNew={openNewResponsibleDialog}
            onEditNew={openNewResponsibleDialog}
            onRemoveNew={itemId ? undefined : clearManualResponsible}
            showPrimaryBadge={!itemId}
          />
          <NewResponsibleDialog
            isOpen={newResponsibleOpen}
            onClose={() => setNewResponsibleOpen(false)}
            onSubmit={applyManualResponsible}
            initialValue={manualResponsible}
            defaultFunction="Responsável pela secretaria"
            entityLabel="secretaria"
          />
        </FormSection>

        <FormSection title="Identidade visual">
          <ImageInputForm
            compact={false}
            defaultVal={defaultValues?.image}
            setImages={setImages}
            setIsUpping={setUploading}
            name={image?.name}
            label={image?.label}
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
        </FormSection>

        <FormActions cancelTo="/secretaries" isLoading={isSubmitting || uploading} />
      </Box>
    </FormShell>
  );
}
